import { useCallback, useMemo } from 'react';
import { ClusterMap, escapeHtml, type MapMarker } from './ClusterMap';
import {
  categoryOf,
  NO_APPOINTMENT_CATEGORY,
  UNKNOWN_CATEGORY_COLOR,
  type PerformanceCategory,
  type TakataDealer,
} from './useTakataDealers';

/**
 * The Takata dealer map — `_Map.cshtml` plus the map half of
 * `stla_takata_dashboard.js`.
 *
 * What is specific to this campaign, and so lives here rather than in
 * `ClusterMap`: rows arrive one per dealer per brand, and they are collapsed into
 * one pin per physical dealer with the per-brand performance inside the popup.
 * The other Stellantis map plots its rows as they come.
 *
 * One difference from the Razor original: values interpolated into the popup are
 * escaped. It concatenated dealer names and addresses from the database straight
 * into innerHTML.
 */

/** Europe, at the zoom the Razor map opened on before fitting to the markers. */
const INITIAL_CENTER: [number, number] = [50.0, 10.0];
const INITIAL_ZOOM = 5;
const MARKER_SIZE = 24;

export interface TakataDealerMapLabels {
  city: string;
  address: string;
  phone: string;
  brand: string;
  performance: string;
  wait: string;
  calls: string;
  days: string;
  noAppointment: string;
  performancePerBrand: string;
}

export interface TakataDealerMapProps {
  dealers: TakataDealer[];
  categories: Record<string, PerformanceCategory>;
  labels: TakataDealerMapLabels;
  resizeToken?: unknown;
}

/** A physical dealer, with one entry per brand it carries. */
interface DealerGroup {
  physOut: string;
  name: string;
  city: string;
  address: string;
  phone: string;
  latitude: number;
  longitude: number;
  brands: {
    brand: string;
    categoryIndex: number | null;
    averageWaitDays: number | null;
    totalCalls: number;
  }[];
}

/**
 * Collapses the flat rows into one entry per physical dealer, so the map draws a
 * single pin rather than a stack of them on the same coordinates.
 */
function groupByPhysOut(dealers: TakataDealer[]): DealerGroup[] {
  const groups = new Map<string, DealerGroup>();

  for (const dealer of dealers) {
    const key = dealer.physOut ?? '';
    let group = groups.get(key);

    if (!group) {
      // Every row for one PhysOut describes the same physical dealer, so the
      // shared fields can come from whichever row arrived first.
      group = {
        physOut: dealer.physOut,
        name: dealer.name,
        city: dealer.city,
        address: dealer.address,
        phone: dealer.phone,
        latitude: dealer.latitude,
        longitude: dealer.longitude,
        brands: [],
      };
      groups.set(key, group);
    }

    group.brands.push({
      brand: dealer.assignedBrand,
      categoryIndex: dealer.categoryIndex,
      averageWaitDays: dealer.averageWaitDays,
      totalCalls: dealer.totalCalls ?? 0,
    });
  }

  for (const group of groups.values()) {
    group.brands.sort((a, b) => (a.brand ?? '').localeCompare(b.brand ?? ''));
  }

  return Array.from(groups.values());
}

/**
 * The category that colours a multi-brand dealer's pin: the worst among the
 * brands that booked an appointment. A dealer excellent for one brand and
 * critical for another reads as critical, which is the point of the map.
 */
function aggregateCategoryIndex(brands: DealerGroup['brands']): number {
  const booked = brands.filter((b) => b.categoryIndex != null && b.categoryIndex > 0);
  return booked.length === 0
    ? NO_APPOINTMENT_CATEGORY
    : Math.max(...booked.map((b) => b.categoryIndex as number));
}

function popupHtml(
  group: DealerGroup,
  categories: Record<string, PerformanceCategory>,
  labels: TakataDealerMapLabels,
): string {
  const headerColor =
    categoryOf(categories, aggregateCategoryIndex(group.brands))?.backgroundColor ??
    UNKNOWN_CATEGORY_COLOR;

  const brandRows = group.brands
    .map((brand) => {
      const category = categoryOf(categories, brand.categoryIndex);
      const wait =
        brand.averageWaitDays != null
          ? `${escapeHtml(brand.averageWaitDays)} ${escapeHtml(labels.days)}`
          : `<span style="color: #6b7280;">${escapeHtml(labels.noAppointment)}</span>`;

      return `
        <tr>
          <td style="padding: 4px 8px 4px 0; font-weight: 600; color: #1f2937; white-space: nowrap;">${escapeHtml(brand.brand || 'N/A')}</td>
          <td style="padding: 4px 8px;">
            <span style="background-color: ${escapeHtml(category?.backgroundColor ?? UNKNOWN_CATEGORY_COLOR)}; color: white; padding: 2px 8px;
                         border-radius: 3px; font-size: 0.8em; white-space: nowrap;">
              ${escapeHtml(category?.label ?? 'N/A')}
            </span>
          </td>
          <td style="padding: 4px 8px; color: #374151; white-space: nowrap;">${wait}</td>
          <td style="padding: 4px 0; color: #374151; white-space: nowrap; text-align: right;">${escapeHtml(brand.totalCalls)}</td>
        </tr>`;
    })
    .join('');

  const multiBrandBadge =
    group.brands.length > 1
      ? `<span style="background: #e0e7ff; color: #3730a3; padding: 1px 8px; border-radius: 10px;
                     font-size: 0.7em; margin-left: 8px; vertical-align: middle;">
           ${group.brands.length} brands
         </span>`
      : '';

  return `
    <div class="dealer-popup" style="min-width: 300px;">
      <h4 style="margin: 0 0 10px 0; color: #1f2937; font-size: 1.1em;
                 border-bottom: 2px solid ${escapeHtml(headerColor)}; padding-bottom: 5px;">
        ${escapeHtml(group.name || 'N/A')}${multiBrandBadge}
      </h4>
      <div style="font-size: 0.9em; color: #374151;">
        <p style="margin: 5px 0;"><strong>${escapeHtml(labels.city)}:</strong> ${escapeHtml(group.city || 'N/A')}</p>
        <p style="margin: 5px 0;"><strong>${escapeHtml(labels.address)}:</strong> ${escapeHtml(group.address || 'N/A')}</p>
        <p style="margin: 5px 0;"><strong>${escapeHtml(labels.phone)}:</strong> ${escapeHtml(group.phone || 'N/A')}</p>

        <div style="margin: 10px 0 6px 0; font-weight: 600; color: #1f2937;">
          ${escapeHtml(labels.performancePerBrand)}
        </div>
        <table style="width: 100%; border-collapse: collapse; font-size: 0.9em;">
          <thead>
            <tr style="border-bottom: 1px solid #e5e7eb; color: #6b7280; font-size: 0.85em;">
              <th style="text-align: left; padding: 4px 8px 4px 0;">${escapeHtml(labels.brand)}</th>
              <th style="text-align: left; padding: 4px 8px;">${escapeHtml(labels.performance)}</th>
              <th style="text-align: left; padding: 4px 8px;">${escapeHtml(labels.wait)}</th>
              <th style="text-align: right; padding: 4px 0;">${escapeHtml(labels.calls)}</th>
            </tr>
          </thead>
          <tbody>${brandRows}</tbody>
        </table>
      </div>
    </div>`;
}

export function TakataDealerMap({
  dealers,
  categories,
  labels,
  resizeToken,
}: TakataDealerMapProps) {
  const markers = useMemo<MapMarker[]>(
    () =>
      groupByPhysOut(dealers).map((group) => {
        const categoryIndex = aggregateCategoryIndex(group.brands);

        return {
          id: group.physOut,
          latitude: group.latitude,
          longitude: group.longitude,
          color: categoryOf(categories, categoryIndex)?.backgroundColor ?? UNKNOWN_CATEGORY_COLOR,
          size: MARKER_SIZE,
          categoryIndex,
          popupHtml: () => popupHtml(group, categories, labels),
        };
      }),
    [dealers, categories, labels],
  );

  const clusterColor = useCallback(
    (categoryIndex: number) =>
      categoryOf(categories, categoryIndex)?.backgroundColor ?? UNKNOWN_CATEGORY_COLOR,
    [categories],
  );

  return (
    <ClusterMap
      markers={markers}
      center={INITIAL_CENTER}
      zoom={INITIAL_ZOOM}
      clusterColor={clusterColor}
      // The filters are how this screen is navigated, so re-framing on each
      // change is the behaviour, not an annoyance.
      fitToMarkers
      resizeToken={resizeToken}
    />
  );
}
